import { HttpClient, HttpContext } from '@angular/common/http'
import { ApplicationRef, Injectable } from '@angular/core'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { filter, take } from 'rxjs/operators'
import { SKIP_PERFORMANCE_METRICS } from '../../interceptors/performance.context'

type PageMetricName = 'ttfb' | 'dom_content_loaded' | 'load' | 'app_ready' | 'route_navigation' | 'lcp'

type RumMetric =
  | {
      kind: 'page'
      metric: PageMetricName
      route: string
      durationMs: number
    }
  | {
      kind: 'api'
      route: string
      method: string
      durationMs: number
      statusCode: number
      attempt: number
    }

@Injectable({ providedIn: 'root' })
export class PerformanceMonitoringService {
  private readonly endpoint = '/api/performance/rum'
  private readonly maxBatchSize = 25
  private pending: RumMetric[] = []
  private flushTimer?: ReturnType<typeof setTimeout>
  private started = false
  private navigationStart?: { id: number; startedAt: number }
  private largestContentfulPaint?: number
  private lcpObserver?: PerformanceObserver

  constructor(private readonly http: HttpClient, private readonly router: Router, private readonly applicationRef: ApplicationRef) {}

  start(): void {
    if (this.started || typeof window === 'undefined') return
    this.started = true
    this.captureDocumentTiming()
    this.observeLargestContentfulPaint()
    this.applicationRef.isStable.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.recordPageMetric('app_ready', this.currentRoute(), performance.now())
    })
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationStart = { id: event.id, startedAt: performance.now() }
      } else if (event instanceof NavigationEnd && this.navigationStart?.id === event.id) {
        this.recordPageMetric(
          'route_navigation',
          this.normalizePath(event.urlAfterRedirects),
          performance.now() - this.navigationStart.startedAt,
        )
        this.navigationStart = undefined
        this.flush()
      }
    })
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('pagehide', this.handlePageHide)
  }

  recordApiRequest(measurement: { method: string; url: string; durationMs: number; statusCode: number; attempt: number }): void {
    const route = this.normalizeApiRoute(measurement.url)
    if (!route) return
    this.enqueue({
      kind: 'api',
      route,
      method: measurement.method,
      durationMs: measurement.durationMs,
      statusCode: measurement.statusCode,
      attempt: measurement.attempt,
    })
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      if (this.largestContentfulPaint !== undefined) {
        this.recordPageMetric('lcp', this.currentRoute(), this.largestContentfulPaint)
        this.largestContentfulPaint = undefined
      }
      this.lcpObserver?.disconnect()
      this.flush(true)
    }
  }

  private readonly handlePageHide = (): void => {
    if (this.largestContentfulPaint !== undefined) {
      this.recordPageMetric('lcp', this.currentRoute(), this.largestContentfulPaint)
      this.largestContentfulPaint = undefined
    }
    this.lcpObserver?.disconnect()
    this.flush(true)
  }

  private captureDocumentTiming(): void {
    const capture = () => {
      const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
      if (!entry) return
      const route = this.normalizePath(window.location.pathname)
      this.recordPageMetric('ttfb', route, entry.responseStart - entry.startTime)
      this.recordPageMetric('dom_content_loaded', route, entry.domContentLoadedEventEnd - entry.startTime)
      this.recordPageMetric('load', route, entry.loadEventEnd - entry.startTime)
    }

    if (document.readyState === 'complete') capture()
    else window.addEventListener('load', capture, { once: true })
  }

  private observeLargestContentfulPaint(): void {
    if (typeof PerformanceObserver === 'undefined') return
    try {
      this.lcpObserver = new PerformanceObserver(list => {
        const entries = list.getEntries()
        const lastEntry = entries[entries.length - 1]
        if (lastEntry) this.largestContentfulPaint = lastEntry.startTime
      })
      this.lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch (error) {
      console.warn('Unable to observe Largest Contentful Paint', error)
    }
  }

  private recordPageMetric(metric: PageMetricName, route: string, durationMs: number): void {
    this.enqueue({ kind: 'page', metric, route: this.normalizePath(route), durationMs })
  }

  private enqueue(metric: RumMetric): void {
    if (!Number.isFinite(metric.durationMs) || metric.durationMs < 0 || metric.durationMs > 600_000) return
    if (this.pending.length >= 250) this.pending.shift()
    this.pending.push(metric)
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = undefined
        this.flush()
      }, 1_000)
    }
  }

  private flush(keepalive = false): void {
    if (this.pending.length === 0 || !localStorage.getItem('JWT_TOKEN')) return
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = undefined
    }

    const events = this.pending.splice(0, this.maxBatchSize)
    if (keepalive) {
      const token = localStorage.getItem('JWT_TOKEN')
      if (!token) return
      void fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ events }),
        credentials: 'same-origin',
        keepalive: true,
      })
        .then(response => {
          if (!response.ok) console.warn('Performance telemetry submission failed', { status: response.status })
        })
        .catch(error => {
          console.warn('Performance telemetry submission failed', error)
        })
      return
    }

    this.http.post(this.endpoint, { events }, { context: new HttpContext().set(SKIP_PERFORMANCE_METRICS, true) }).subscribe({
      error: (error: { status?: number }) => {
        console.warn('Performance telemetry submission failed', { status: error.status ?? 0 })
      },
      complete: () => {
        if (this.pending.length > 0) this.flush()
      },
    })
  }

  private currentRoute(): string {
    return this.router.url || window.location.pathname
  }

  private normalizeApiRoute(url: string): string | undefined {
    try {
      const pathname = new URL(url, window.location.origin).pathname
      if (!pathname.startsWith('/api/')) return undefined
      const [api, controller] = pathname.split('/').filter(Boolean)
      return controller ? `/${api}/${controller}` : undefined
    } catch {
      return undefined
    }
  }

  private normalizePath(path: string): string {
    const pathname = path.split(/[?#]/, 1)[0] || '/'
    const segments = pathname
      .split('/')
      .filter(Boolean)
      .slice(0, 1)
      .map(segment => {
        if (
          /^\d+$/.test(segment) ||
          /^[a-f\d]{24}$/i.test(segment) ||
          /^[\da-f]{8}-(?:[\da-f]{4}-){3}[\da-f]{12}$/i.test(segment) ||
          (segment.length >= 16 && /[a-z]/i.test(segment) && /\d/.test(segment)) ||
          segment.length > 48
        ) {
          return ':id'
        }
        return segment.replace(/[^a-zA-Z0-9_.:-]/g, '_')
      })
    return `/${segments.join('/')}`.slice(0, 120) || '/'
  }
}
